import { createContext, useContext, useState } from 'react';
import { T, useGT } from 'gt-react';
import { BracketsCurlyIcon } from '@phosphor-icons/react';
import type { ReportDateRange } from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConfigRow } from './charts/shared';
import type { ReportComponentConfig } from './types';

/**
 * Opens the surface's whole-component JSON editor on a component (the dialog
 * outlives the settings popover); null where the surface has none.
 */
export const ComponentJsonEditorContext = createContext<
  ((componentId: string) => void) | null
>(null);

/** Range pins offered in a sourced component's settings. */
const PIN_DAYS = [7, 14, 30, 90] as const;

function pinValue(range: ReportDateRange | undefined): string {
  if (!range) return 'page';
  return range.kind === 'last_days' ? String(range.days) : 'custom';
}

function pinRange(value: string): ReportDateRange | undefined {
  const days = Number(value);
  return Number.isInteger(days) && days > 0
    ? { kind: 'last_days', days }
    : undefined;
}

/**
 * The settings of a component with its own `source`: rename it, pin its
 * source to its own date range, or open the whole component as JSON.
 */
export function SourceSettingsForm({
  component,
  onUpdate,
  onClose,
}: {
  component: ReportComponentConfig;
  onUpdate: (updated: ReportComponentConfig) => void;
  onClose?: () => void;
}) {
  const gt = useGT();
  const editJson = useContext(ComponentJsonEditorContext);
  const [title, setTitle] = useState(component.title ?? '');
  const { source } = component;
  const pin = pinValue(source?.range);
  const commitTitle = () => {
    const next = title.trim();
    if (next.length > 0 && next !== component.title)
      onUpdate({ ...component, title: next });
  };
  return (
    <div className="flex flex-col gap-3">
      <ConfigRow label={gt('Title', { context: 'report widget setting' })}>
        <Input
          value={title}
          className="h-8 text-xs"
          onChange={(event) => setTitle(event.target.value)}
          onBlur={commitTitle}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commitTitle();
          }}
        />
      </ConfigRow>
      {source && (
        <ConfigRow
          label={gt('Date range', { context: 'report widget setting' })}
        >
          <Select
            value={pin}
            onValueChange={(value) => {
              const { range: _range, ...rest } = source;
              const range = pinRange(value);
              onUpdate({
                ...component,
                source: range ? { ...rest, range } : rest,
              });
            }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="page">
                <T context="report widget range setting, follow the page">
                  The page's range
                </T>
              </SelectItem>
              {PIN_DAYS.map((days) => (
                <SelectItem key={days} value={String(days)}>
                  {gt('Last {days} days', {
                    days,
                    context: 'report widget range setting',
                  })}
                </SelectItem>
              ))}
              {pin === 'custom' && (
                <SelectItem value="custom" disabled>
                  <T context="report widget range setting, set in its JSON">
                    Custom dates
                  </T>
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </ConfigRow>
      )}
      {editJson && (
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 text-xs"
          onClick={() => {
            onClose?.();
            editJson(component.id);
          }}
        >
          <BracketsCurlyIcon className="h-3.5 w-3.5" />
          <T context="button, edit a report widget's whole config as JSON">
            Edit JSON
          </T>
        </Button>
      )}
    </div>
  );
}
